import React, { useState, useEffect } from 'react';
import {
  Form,
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Tag,
  Table,
  Input,
  DatePicker,
  Alert,
  Descriptions,
  List,
  Avatar,
  Timeline,
  Modal
} from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { gqlClient } from '../../../helpers/gqlProvider';
import { SUPPLIER_PORTAL_SUPPLIERS_QUERY } from '../../sourcing/queries';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ReviewTabProps {
  form: any;
  formData: any;
  sourcingData: any;
  onPublish: (values: any) => Promise<void>;
  loading: boolean;
  isPublishing?: boolean;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({
  formData,
  sourcingData,
  onPublish,
  loading,
  isPublishing = false,
}) => {
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [publishForm] = Form.useForm();
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  // Initialize and update publish form with data
  useEffect(() => {
    if (isPublishing && (formData || sourcingData)) {
      publishForm.setFieldsValue({
        title: formData?.title || sourcingData?.title,
        description: formData?.description || sourcingData?.description,
      });
    }
  }, [isPublishing, formData, sourcingData, publishForm]);

  // Fetch suppliers from the backend API
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await gqlClient.query(SUPPLIER_PORTAL_SUPPLIERS_QUERY, {});
        if (response.data?.supplierPortalSuppliers) {
          setSuppliers(response.data.supplierPortalSuppliers);
        } else {
          console.error('No suppliers data received:', response);
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      } finally {
        setSuppliersLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  const handlePublish = async () => {
    try {
      const values = await publishForm.validateFields();
      await onPublish({
        ...formData,
        ...values,
      });
    } catch (error) {
      console.error('Publish validation error:', error);
    }
  };

  // Mock function to get template by ID - in real implementation, this would be from props or API
  const getTemplateById = (id: string) => {
    const templates = [
      { id: '1', name: 'Standard RFQ Template', sourcingType: 'rfq', biddingType: 'ncb' },
      { id: '2', name: 'IT Services RFP Template', sourcingType: 'rfp', biddingType: 'icb' },
    ];
    return templates.find(t => t.id === id);
  };

  const formValues = formData;
  const selectedTemplate = formValues.templateId ?
    getTemplateById(formValues.templateId) : null;

  const getSuppliersByIds = (ids: string[]) => {
    return suppliers.filter(s => ids.includes(s.id));
  };

  const selectedSuppliers = getSuppliersByIds(formValues.invitedSupplierIds || []);

  const fieldColumns = [
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Required',
      dataIndex: 'isRequired',
      key: 'isRequired',
      render: (isRequired: boolean) => (
        <Tag color={isRequired ? 'red' : 'green'}>
          {isRequired ? 'Required' : 'Optional'}
        </Tag>
      ),
    },
  ];

  const fileRequirementColumns = [
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (type: string) => <Tag color="purple">{type}</Tag>,
    },
    {
      title: 'Required',
      dataIndex: 'isRequired',
      key: 'isRequired',
      render: (isRequired: boolean) => (
        <Tag color={isRequired ? 'red' : 'green'}>
          {isRequired ? 'Required' : 'Optional'}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      {/* Sourcing Summary Component */}
      <Card title="Sourcing Summary" style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={12}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Title">
                <Text strong>{formData?.title || sourcingData?.title || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                <Paragraph ellipsis={{ rows: 2, tooltip: formData?.description || sourcingData?.description }}>
                  {formData?.description || sourcingData?.description || 'No description available'}
                </Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="blue">{sourcingData?.status?.toUpperCase() || 'DRAFT'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {sourcingData?.createdAt ? dayjs(sourcingData.createdAt).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {sourcingData?.createdBy?.name || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Currency">
                <Text strong>{sourcingData?.currency || 'ETB'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {sourcingData?.currency || 'ETB'} {sourcingData?.totalAmount?.toLocaleString() || '0'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Items Count">
                <Text strong>{sourcingData?.items?.length || 0} items</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {sourcingData?.category || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {sourcingData?.department || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        {/* Items Details */}
        {sourcingData?.items && sourcingData.items.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Items Details</Title>
            <Table
              dataSource={sourcingData.items}
              columns={[
                {
                  title: 'Item Name',
                  dataIndex: 'name',
                  key: 'name',
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: true,
                  render: (description: string) => description || 'No description',
                },
                {
                  title: 'Specifications',
                  dataIndex: 'specifications',
                  key: 'specifications',
                  ellipsis: true,
                  render: (specs: string) => specs || 'No specifications',
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  render: (quantity: number) => `${quantity || 0}`,
                },
                {
                  title: 'Unit',
                  dataIndex: 'unit',
                  key: 'unit',
                  render: (unit: string) => unit || 'N/A',
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  render: (price: number) => `${sourcingData?.currency || 'ETB'} ${price?.toLocaleString() || '0'}`,
                },
                {
                  title: 'Total Price',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  render: (price: number) => `${sourcingData?.currency || 'ETB'} ${price?.toLocaleString() || '0'}`,
                },
                {
                  title: 'Tags',
                  dataIndex: 'tags',
                  key: 'tags',
                  render: (tags: string[]) => (
                    <div>
                      {tags && tags.length > 0 ? (
                        tags.map((tag, index) => (
                          <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                            {tag}
                          </Tag>
                        ))
                      ) : (
                        <Text type="secondary">No tags</Text>
                      )}
                    </div>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </div>
        )}

        {/* Attachments */}
        {sourcingData?.attachments && sourcingData.attachments.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Attachments</Title>
            <List
              dataSource={sourcingData.attachments}
              renderItem={(attachment: any) => (
                <List.Item key={attachment.fileId}>
                  <List.Item.Meta
                    avatar={<FileTextOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                    title={
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff', textDecoration: 'none' }}
                      >
                        {attachment.originalName}
                      </a>
                    }
                    description={
                      <div>
                        <Text type="secondary">
                          {attachment.category || 'Document'} • {attachment.mimeType || 'Unknown type'} • {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {/* Evaluation Criteria - Only show for RFP */}
        {sourcingData?.type === 'RFP' && sourcingData?.evaluationCriterias && sourcingData.evaluationCriterias.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Evaluation Criteria</Title>
            <Table
              dataSource={sourcingData.evaluationCriterias}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Criterion Name',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string) => <Text strong>{name}</Text>,
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: true,
                  render: (description: string) => description || <Text type="secondary">No description</Text>,
                },
                {
                  title: 'Weight (%)',
                  dataIndex: 'weight',
                  key: 'weight',
                  width: 120,
                  render: (weight: number) => (
                    <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {weight}%
                    </Tag>
                  ),
                },
                {
                  title: 'Visible to Suppliers',
                  dataIndex: 'visibleToSuppliers',
                  key: 'visibleToSuppliers',
                  width: 160,
                  render: (visibility: string) => {
                    let color = 'default';
                    let text = visibility;
                    switch (visibility) {
                      case 'FULL':
                        color = 'green';
                        text = 'Full';
                        break;
                      case 'PARTIAL':
                        color = 'orange';
                        text = 'Partial';
                        break;
                      case 'NONE':
                        color = 'red';
                        text = 'None';
                        break;
                    }
                    return <Tag color={color}>{text}</Tag>;
                  },
                },
                {
                  title: 'Threshold',
                  dataIndex: 'threshold',
                  key: 'threshold',
                  width: 120,
                  render: (threshold?: number) => threshold ? (
                    <Text>{threshold}</Text>
                  ) : (
                    <Text type="secondary">N/A</Text>
                  ),
                },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong>Total Weight:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Tag color="green" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {sourcingData.evaluationCriterias.reduce((sum: number, criteria: any) => sum + (criteria.weight || 0), 0)}%
                    </Tag>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        )}
      </Card>

      <Alert
        message="Review & Publish"
        description="Review all configuration details before publishing to the supplier portal."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={24}>
        <Col span={12}>
          <Card title="Sourcing Configuration" style={{ marginBottom: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Template">
                <Tag color="blue">{selectedTemplate?.name || 'Not selected'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Sourcing Type">
                <Tag color="green">{formValues.sourcingType?.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Bidding Type">
                <Tag color="orange">{formValues.biddingType?.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Invitation Mode">
                <Tag color="purple">
                  {formValues.invitationMode === 'CLOSED' ? 'Closed Sourcing' : 'Open Sourcing'}
                </Tag>
              </Descriptions.Item>
              {formValues.isPublic && (
                <Descriptions.Item label="Visibility">
                  <Tag color="cyan" icon={<GlobalOutlined />}>
                    Public
                  </Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Timeline & Details" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Due Date:</Text>
                <br />
                <Text type="secondary">Please set the submission deadline</Text>
              </div>
              <div>
                <Text strong>Estimated Value:</Text>
                <br />
                <Text type="secondary">
                  {sourcingData?.currency || 'ETB'} {sourcingData?.totalAmount?.toLocaleString()}
                </Text>
              </div>
              <div>
                <Text strong>Items:</Text>
                <br />
                <Text type="secondary">{sourcingData?.items?.length || 0} items</Text>
              </div>
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Suppliers" style={{ marginBottom: 24 }}>
            {formValues.invitationMode === 'CLOSED' ? (
              suppliersLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">Loading suppliers...</Text>
                </div>
              ) : selectedSuppliers.length > 0 ? (
                <List
                  dataSource={selectedSuppliers}
                  renderItem={(supplier) => (
                    <List.Item key={supplier.id}>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={supplier.companyName}
                        description={
                          <div>
                            <div><MailOutlined /> {supplier.contactInfo.email}</div>
                            <div><PhoneOutlined /> {supplier.contactInfo.phone}</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  No suppliers selected
                </div>
              )
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <GlobalOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: 16 }} />
                <div>
                  <Text strong>Open Sourcing</Text>
                  <br />
                  <Text type="secondary">
                    Visible to all registered suppliers on the platform
                  </Text>
                </div>
              </div>
            )}
          </Card>

          <Card title="Fields" style={{ marginBottom: 24 }}>
            <Table
              dataSource={formValues.fields || []}
              columns={fieldColumns}
              rowKey="name"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No fields configured' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="File Requirements" style={{ marginBottom: 24 }}>
        <Table
          dataSource={formValues.fileRequirements || []}
          columns={fileRequirementColumns}
          rowKey="name"
          pagination={false}
          size="small"
          locale={{ emptyText: 'No file requirements configured' }}
        />
      </Card>


      {formValues.customFileRequirements && formValues.customFileRequirements.length > 0 && (
        <Card title="Custom File Requirements" style={{ marginBottom: 24 }}>
          <Table
            dataSource={formValues.customFileRequirements}
            columns={fileRequirementColumns}
            rowKey={( index) => `custom-file-${index}`}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      <Card title="Publishing Information" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message="Ready to Publish"
            description="Once published, this sourcing will be visible to suppliers and they will be able to submit bids."
            type="success"
            showIcon
          />

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={() => setPublishModalVisible(true)}
              loading={loading}
            >
              Publish to Supplier Portal
            </Button>
          </div>
        </Space>
      </Card>

      {/* Publish Confirmation Modal */}
      <Modal
        title="Publish Sourcing to Supplier Portal"
        visible={publishModalVisible}
        onOk={handlePublish}
        onCancel={() => {
          setPublishModalVisible(false);
          publishForm.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form form={publishForm} layout="vertical">
          <Alert
            message="Final Confirmation"
            description="Please review the publishing details below before proceeding."
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Sourcing Title"
                name="title"
                rules={[{ required: true, message: 'Please enter sourcing title' }]}
              >
                <Input
                  placeholder="Enter sourcing title for portal"
                  disabled={isPublishing}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Due Date"
                name="dueDate"
                rules={[{ required: true, message: 'Please select due date' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the sourcing opportunity for suppliers..."
              disabled={isPublishing}
            />
          </Form.Item>

          <div style={{ marginTop: 16 }}>
            <Title level={5}>What happens next?</Title>
            <Timeline>
              <Timeline.Item color="blue">
                Sourcing will be published to the supplier portal
              </Timeline.Item>
              <Timeline.Item color="blue">
                {formValues.invitationMode === 'CLOSED'
                  ? 'Selected suppliers will receive invitations'
                  : 'All registered suppliers will see the sourcing'
                }
              </Timeline.Item>
              <Timeline.Item color="blue">
                Suppliers can view details and submit bids
              </Timeline.Item>
              <Timeline.Item color="blue">
                You will receive notifications for new bids
              </Timeline.Item>
            </Timeline>
          </div>
        </Form>
      </Modal>
    </div>
  );
};